import { PADDLE, VIEW } from '../core/config.js';

/**
 * Every control scheme funnels into one place: a desired paddle x plus a set
 * of edge-triggered actions. Mouse, touch drag, on-screen buttons and keyboard
 * all stay live at once, and the last device you touched wins.
 */

const LEFT_KEYS = new Set(['ArrowLeft', 'KeyA']);
const RIGHT_KEYS = new Set(['ArrowRight', 'KeyD']);
const FIRE_KEYS = new Set(['Space', 'ArrowUp', 'KeyW', 'Enter', 'NumpadEnter']);
const PAUSE_KEYS = new Set(['KeyP', 'Escape']);

export function createInput({ canvas, toLogicalX, onFire, onPause, onAnyInput }) {
  const state = {
    /** Where the player wants the paddle centre, in logical pixels. */
    targetX: VIEW.W / 2,
    keyLeft: false,
    keyRight: false,
    btnLeft: false,
    btnRight: false,
    pointerDown: false,
    /** 'pointer' when tracking the cursor/finger, 'keys' when nudging. */
    mode: 'pointer',
    grabOffset: 0,
  };

  const listeners = [];
  const on = (target, type, handler, opts) => {
    target.addEventListener(type, handler, opts);
    listeners.push(() => target.removeEventListener(type, handler, opts));
  };

  function touched() {
    onAnyInput?.();
  }

  function pointerTarget(event) {
    return toLogicalX(event.clientX);
  }

  on(canvas, 'pointerdown', (event) => {
    touched();
    canvas.setPointerCapture?.(event.pointerId);
    state.pointerDown = true;
    state.mode = 'pointer';

    const logical = pointerTarget(event);
    // Grabbing near the paddle keeps a finger from snapping it away; a tap
    // further off jumps straight to that spot.
    const distance = Math.abs(logical - state.targetX);
    state.grabOffset = distance < 70 ? state.targetX - logical : 0;
    state.targetX = logical + state.grabOffset;

    if (event.pointerType === 'mouse') onFire?.();
    event.preventDefault();
  });

  on(canvas, 'pointermove', (event) => {
    if (event.pointerType === 'mouse' && !state.pointerDown) {
      state.mode = 'pointer';
      state.targetX = pointerTarget(event);
      return;
    }
    if (!state.pointerDown) return;
    state.mode = 'pointer';
    state.targetX = pointerTarget(event) + state.grabOffset;
    event.preventDefault();
  });

  const endPointer = (event) => {
    state.pointerDown = false;
    state.grabOffset = 0;
    if (event?.pointerId != null) canvas.releasePointerCapture?.(event.pointerId);
  };
  on(canvas, 'pointerup', endPointer);
  on(canvas, 'pointercancel', endPointer);
  on(canvas, 'contextmenu', (event) => event.preventDefault());

  on(window, 'keydown', (event) => {
    if (event.repeat && !FIRE_KEYS.has(event.code)) return;
    const code = event.code;

    if (LEFT_KEYS.has(code) || RIGHT_KEYS.has(code)) {
      touched();
      state.mode = 'keys';
      if (LEFT_KEYS.has(code)) state.keyLeft = true;
      else state.keyRight = true;
      event.preventDefault();
      return;
    }

    if (FIRE_KEYS.has(code)) {
      // Let the browser handle Enter/Space when a real button has focus.
      if (isInteractive(document.activeElement) && code !== 'ArrowUp') return;
      touched();
      if (!event.repeat) onFire?.();
      event.preventDefault();
      return;
    }

    if (PAUSE_KEYS.has(code)) {
      touched();
      onPause?.();
      event.preventDefault();
    }
  });

  on(window, 'keyup', (event) => {
    if (LEFT_KEYS.has(event.code)) state.keyLeft = false;
    if (RIGHT_KEYS.has(event.code)) state.keyRight = false;
  });

  on(window, 'blur', () => {
    state.keyLeft = false;
    state.keyRight = false;
    state.btnLeft = false;
    state.btnRight = false;
    state.pointerDown = false;
  });

  function isInteractive(el) {
    return el instanceof HTMLElement && /^(BUTTON|A|INPUT|SELECT|TEXTAREA)$/.test(el.tagName);
  }

  return {
    state,

    /** On-screen d-pad buttons call this. */
    setButton(which, pressed) {
      touched();
      state.mode = 'keys';
      if (which === 'left') state.btnLeft = pressed;
      if (which === 'right') state.btnRight = pressed;
    },

    /** Jump the paddle to a spot without a pointer event (used on respawn). */
    setTarget(x) {
      state.targetX = x;
    },

    /**
     * Resolve this frame's desired paddle centre.
     * @param {number} dt
     * @param {object} paddle
     */
    resolve(dt, paddle) {
      const dir =
        (state.keyLeft || state.btnLeft ? -1 : 0) + (state.keyRight || state.btnRight ? 1 : 0);

      if (dir !== 0) {
        state.mode = 'keys';
        state.targetX = paddle.cx + dir * PADDLE.keySpeed * dt;
      }
      return state.targetX;
    },

    destroy() {
      listeners.forEach((off) => off());
      listeners.length = 0;
    },
  };
}
